import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { InvoiceListRecord } from '@spike/invoice-model';
import { FieldError } from '@spike/model';
import { isEmailValid } from '@spike/validations';
import ControlledTextField2 from 'components/UI/ControlledTextField2';
import Button from 'components/UI/V3/Button/Button';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import { mediaQueries } from 'hooks/useBreakpoints';
import { isEmpty } from 'lodash';
import { isTemporalEmail } from 'utils/ValidationUtils';
import styles from './SendInvoiceModal.module.scss';

export interface SendInvoiceModalProps {
    invoice: InvoiceListRecord | null;
    sending?: boolean;
    zIndex?: number;
    onClose: () => void;
    onSend: (email: string) => void;
}

const isTemporalInvoiceEmail = (email?: string) => {
    const value = (email ?? '').toLowerCase();
    return value.includes('temporal.cuddlesapp.com') || isTemporalEmail(value);
};

const getInitialEmail = (email?: string) => (isTemporalInvoiceEmail(email) ? '' : email ?? '');

const SendInvoiceModalContent: FunctionComponent<{
    invoice: InvoiceListRecord;
    sending?: boolean;
    zIndex?: number;
    onClose: () => void;
    onSend: (email: string) => void;
}> = ({ invoice, sending = false, zIndex, onClose, onSend }) => {
    const isMobile = useMediaQuery(`(max-width:${mediaQueries.down.sm})`);
    const [email, setEmail] = useState(() => getInitialEmail(invoice.customer.email));
    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const validateEmail = (value: string): Array<FieldError> => {
        const trimmed = value.trim();
        const nextErrors: Array<FieldError> = [];

        if (!trimmed || isEmpty(trimmed) || isTemporalInvoiceEmail(trimmed)) {
            nextErrors.push({
                fieldName: 'email',
                errorMessage: 'Email is required'
            });
        } else if (!isEmailValid(trimmed)) {
            nextErrors.push({
                fieldName: 'email',
                errorMessage: 'Email incorrect format'
            });
        }

        return nextErrors;
    };

    useEffect(() => {
        setErrors(validateEmail(email));
    }, [email]);

    const canSend = useMemo(() => {
        const trimmed = email.trim();
        return trimmed.length > 0 && isEmailValid(trimmed) && !isTemporalInvoiceEmail(trimmed);
    }, [email]);

    const sendDisabled = sending || !canSend;

    const handleSend = () => {
        const nextErrors = validateEmail(email);

        if (nextErrors.length > 0) {
            setErrors(nextErrors);
            return;
        }

        onSend(email.trim());
    };

    return (
        <GenericModal
            fullScreen={isMobile}
            zIndex={zIndex}
            onClose={onClose}
            footer={
                <div className={styles.footer}>
                    <div className={styles.footerAction}>
                        <Button
                            size="large"
                            className={styles.button}
                            label="Cancel"
                            onClick={onClose}
                            variant="black"
                            disabled={sending}
                        />
                    </div>
                    <div className={styles.footerAction}>
                        <Button
                            label={sending ? 'Sending...' : 'Send'}
                            size="large"
                            className={styles.button}
                            onClick={handleSend}
                            variant="green"
                            isLoading={sending}
                            disabled={sendDisabled}
                        />
                    </div>
                </div>
            }
            className={styles.modal}
        >
            <div className={styles.content}>
                <span className={styles.title}>Send Invoice</span>
                <ControlledTextField2
                    label="Email"
                    placeholder="Email Address"
                    name="email"
                    value={email}
                    onChange={setEmail}
                    autoComplete="off"
                    errors={errors}
                    disabled={sending}
                    className={styles.emailField}
                />
            </div>
        </GenericModal>
    );
};

export const SendInvoiceModal: FunctionComponent<SendInvoiceModalProps> = ({
    invoice,
    sending,
    zIndex,
    onClose,
    onSend
}) => {
    if (!invoice) {
        return null;
    }

    return (
        <SendInvoiceModalContent
            key={invoice.uuid}
            invoice={invoice}
            sending={sending}
            zIndex={zIndex}
            onClose={onClose}
            onSend={onSend}
        />
    );
};

export default SendInvoiceModal;
